import React, { useState } from 'react';
import { supabase } from '../integrations/client.ts';
import '../styles/SettingsModal.css';

const SettingsModal = ({ currentUser, onClose, onSettingsSaved }) => {
    const [vocabularyLevel, setVocabularyLevel] = useState(
        currentUser?.vocabulary_level || currentUser?.vocabularyLevel || 'auto'
    );
    const [courseLength, setCourseLength] = useState(
        currentUser?.course_length || currentUser?.courseLength || 0
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    const vocabularyLevels = [
        { value: 'simple', label: 'Simple' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'phd', label: 'PhD' },
        { value: 'auto', label: 'Auto' }
    ];

    const courseLengths = [
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 0, label: 'Auto' }
    ];

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            const { error: updateError } = await supabase
                .from('profiles')
                .update({
                    vocabulary_level: vocabularyLevel,
                    course_length: courseLength
                })
                .eq('id', currentUser.id);

            if (updateError) {
                throw updateError;
            }

            if (onSettingsSaved) {
                await onSettingsSaved();
            }

            // Show notification
            setShowNotification(true);

            // Close modal after showing notification for 2 seconds
            setTimeout(() => {
                setShowNotification(false);
                onClose();
            }, 2000);
        } catch (err) {
            console.error('Error saving settings:', err);
            setError('Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const getVocabularyDescription = () => {
        switch (vocabularyLevel) {
            case 'simple':
                return 'Uses everyday language suitable for beginners with no prior knowledge.';
            case 'intermediate':
                return 'Clear technical language with moderate complexity for learners with some background.';
            case 'advanced':
                return 'Advanced terminology assuming a strong foundation in the subject.';
            case 'phd':
                return 'Expert-level academic language with advanced concepts and research terminology.';
            case 'auto':
                return 'AI automatically determines the appropriate complexity level based on the subject matter.';
            default:
                return '';
        }
    };

    const getCourseLengthDescription = () => {
        if (courseLength === 0) {
            return 'AI automatically determines the optimal number of chapters (typically 3-8) based on subject complexity.';
        } else if (courseLength >= 6) {
            return 'Note: Higher chapter counts on broad subjects may result in some redundant content. Best for comprehensive, in-depth topics.';
        } else {
            return `Course will contain exactly ${courseLength} chapters covering the essential concepts.`;
        }
    };

    return (
        <div className="settings-modal-overlay" onClick={onClose}>
            <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose} aria-label="Close">
                    ×
                </button>

                <div className="settings-header">
                    <h2>Course Generation Settings</h2>
                    <p className="settings-subtitle">
                        Customize how AI generates your courses. These settings apply to all new courses you create.
                    </p>
                </div>

                <div className="settings-content">
                    <div className="setting-section">
                        <div className="setting-label-row">
                            <label className="setting-label">Vocabulary Level</label>
                            <span className="setting-value">{vocabularyLevels.find(v => v.value === vocabularyLevel)?.label}</span>
                        </div>
                        <div className="slider-container">
                            <input
                                type="range"
                                min="0"
                                max="4"
                                value={vocabularyLevels.findIndex(v => v.value === vocabularyLevel)}
                                onChange={(e) => setVocabularyLevel(vocabularyLevels[parseInt(e.target.value)].value)}
                                className="setting-slider"
                            />
                            <div className="slider-labels">
                                {vocabularyLevels.map((level) => (
                                    <span key={level.value} className="slider-label">
                                        {level.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <p className="setting-description">{getVocabularyDescription()}</p>
                    </div>

                    <div className="setting-section">
                        <div className="setting-label-row">
                            <label className="setting-label">Course Length (Chapters)</label>
                            <span className="setting-value">
                                {courseLength === 0 ? 'Auto' : `${courseLength} chapters`}
                            </span>
                        </div>
                        <div className="slider-container">
                            <input
                                type="range"
                                min="0"
                                max="6"
                                value={courseLengths.findIndex(c => c.value === courseLength)}
                                onChange={(e) => setCourseLength(courseLengths[parseInt(e.target.value)].value)}
                                className="setting-slider"
                            />
                            <div className="slider-labels">
                                {courseLengths.map((length) => (
                                    <span key={length.value} className="slider-label">
                                        {length.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <p className="setting-description">{getCourseLengthDescription()}</p>
                    </div>

                    <div className="setting-section setting-disabled">
                        <div className="setting-label-row">
                            <label className="setting-label">Learning Visuals</label>
                            <span className="coming-soon-badge">Coming Soon</span>
                        </div>
                        <p className="setting-description">
                            Interactive visualizations for enhanced learning experiences.
                        </p>
                    </div>

                    {error && (
                        <div className="settings-error">
                            {error}
                        </div>
                    )}
                </div>

                <div className="settings-footer">
                    <button
                        className="settings-btn settings-btn-cancel"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="settings-btn settings-btn-save"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {showNotification && (
                <div className="settings-notification">
                    Course Generating Settings Updated
                </div>
            )}
        </div>
    );
};

export default SettingsModal;
